import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Calendar, MapPin, Phone, Play } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import useYouTubeVideos from '../hooks/useYouTubeVideos';

const Home = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { videos, loading } = useYouTubeVideos(6);
  const ministerios = [
    {
      title: 'Louvor e Adoração',
      description: 'Ministério dedicado a adorar a Deus através da música.',
      icon: (
        <img
          src="/MEVAMmusic.webp"
          alt="MEVAM Música"
          className="mx-auto h-50 w-50 object-contain"
          style={{ height: '10rem', width: '10rem' }}
        />
      ),
    },
    {
      title: 'Jovens',
      description: 'Ministério focado no desenvolvimento espiritual dos jovens.',
      icon: (
        <img
          src="/logojovens.webp"
          alt="MEVAM Jovem"
          className="mx-auto h-50 w-50 object-contain"
          style={{ height: '10rem', width: '10rem' }}
        />
      ),
    },
    {
      title: 'Homens',
      description: 'Grupo de Homens MEVAM.',
      icon: (
        <img
          src="/Logohomens.webp"
          alt="MEVAM Homens"
          className="mx-auto h-50 w-50 object-contain"
          style={{ height: '10rem', width: '10rem' }}
        />
      ),
    },
    {
      title: 'KIDS',
      description: 'Ministério focado no desenvolvimento espiritual das crianças.',
      icon: (
        <img
          src="/logokids.webp"
          alt="MEVAM KIDS"
          className="mx-auto h-50 w-50 object-contain"
          style={{ height: '10rem', width: '10rem' }}
        />
      ),
    },
  ];

  const eventos = [
    {
      titulo: 'Culto de Celebração',
      data: 'Domingos',
      horario: '19:00',
      descricao: 'Nossos domingos de celebração tem sido incríveis. Venha ter uma experiência única com Jesus! Estamos também no online através do canal MEVAM Brasília no YouTube. Um momento especial dedicado às famílias, com ensinamentos práticos sobre relacionamentos e princípios bíblicos.',
    },
    {
      titulo: 'Culto de Profético',
      data: 'Quinta-feiras',
      horario: '19:30',
      descricao: 'Nosso culto profético é um momento especial de busca e renovação espiritual. Venha receber uma palavra profética para sua vida!',
    },
    {
      titulo: 'PG Familiar | MEVAM Academy',
      data: 'Terças-feiras',
      horario: '20:00',
      descricao: 'Estudo sistemático da Palavra de Deus.',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-[#030B2E] via-[#030B2E] to-[#17E6B3]/20 bg-cover bg-center bg-pattern">
        <div className="absolute inset-0 bg-[#030B2E]/70"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Bem-vindo ao
                <span className="block text-gradient bg-gradient-to-r from-secondary-500 to-secondary-300 bg-clip-text text-transparent">
                  MEVAM Brasília
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl">
                <strong>Ministério Evangelístico Vinde Amados Meus</strong> - Uma igreja que ama, 
                serve e edifica vidas no coração do Brasil.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/sobre" className="btn-secondary inline-flex items-center justify-center">
                  Conheça Nossa História
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link to="/contato" className="btn-secondary inline-flex items-center justify-center">
                  Venha nos Visitar
                  <MapPin className="ml-2" size={20} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Palavras - Vídeos do YouTube */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Palavras
              </h2>
              <p className="text-[#B8C4E3] text-lg max-w-2xl mx-auto">
                Mensagens transformadoras que edificam, inspiram e renovam sua fé
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {loading ? (
              // Skeleton loading
              [1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-2xl bg-[#030B2E] border border-white/10">
                    <div className="relative aspect-video bg-gradient-to-br from-[#030B2E] via-[#17E6B3]/10 to-[#030B2E] animate-pulse">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm"></div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="h-6 bg-white/10 rounded animate-pulse"></div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-[#030B2E] border border-white/10 hover:border-[#17E6B3]/50 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-[#17E6B3]/20">
                    <div className="relative aspect-video overflow-hidden">
                      {/* Thumbnail real do YouTube */}
                      <img 
                        src={video.thumbnail}
                        alt={video.titulo}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          // Fallback para thumbnail de qualidade média se maxresdefault falhar
                          e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                        }}
                      />
                      
                      {/* Overlay com botão play */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 flex items-center justify-center opacity-100 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#17E6B3]/80 group-hover:scale-110 transition-all duration-300">
                          <Play className="text-white ml-1" size={28} fill="white" />
                        </div>
                      </div>
                      
                      {/* Badge da categoria */}
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-[#17E6B3]/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                          {video.categoria}
                        </span>
                      </div>
                      
                      {/* Data */}
                      <div className="absolute bottom-3 right-3">
                        <span className="text-white text-xs bg-black/50 backdrop-blur-sm px-2 py-1 rounded-lg">
                          {video.data}
                        </span>
                      </div>
                    </div>
                    
                    {/* Título */}
                    <div className="p-5">
                      <h3 className="text-white font-bold text-lg line-clamp-2 group-hover:text-[#17E6B3] transition-colors">
                        {video.titulo}
                      </h3>
                      {video.pregador && (
                        <p className="text-[#B8C4E3] text-sm mt-2">
                          {video.pregador}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          <div className="text-center">
            <Link 
              to="/palavras" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#17E6B3]/30 transition-all duration-300"
            >
              Ver todas as palavras
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sobre Nós */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Quem Somos
              </h2>
              <p className="text-[#B8C4E3] text-lg mb-6">
                Somos uma igreja que acredita em um evangelho que se aproxima, pensamos em um 
                evangelho que não desiste das pessoas. Uma igreja onde o pecado será tratado 
                com rigor, mas o pecador será tratado com amor.
              </p>
              <p className="text-[#B8C4E3] text-lg mb-6">
                Somos um lugar de encorajadores. Deus nos disse para segurar as mãos uns dos 
                outros e falar "bora", vamos chegar até o final juntos, ninguém irá ficar pra trás.
              </p>
              <div className="flex items-center space-x-4 mb-8">
                <div className="flex items-center space-x-2">
                  <Heart className="text-[#17E6B3]" size={20} />
                  <span className="font-semibold text-white">Grande Para Servir</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="text-[#17E6B3]" size={20} />
                  <span className="font-semibold text-white">Pequena Para Se Importar</span>
                </div>
              </div>
              <Link to="/sobre" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center shadow-lg shadow-[#17E6B3]/30">
                Saiba Mais
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#17E6B3]/20 to-[#030B2E] rounded-2xl p-8 text-white border border-[#17E6B3]/30 shadow-xl shadow-[#17E6B3]/10">
                <h3 className="text-2xl font-bold mb-4">Nossa Missão</h3>
                <p className="text-lg mb-6 text-[#B8C4E3]">
                  Levar cada pessoa a um relacionamento público e crescente com Jesus, 
                  impactando Brasília com o amor de Deus.
                </p>
                <div className="bg-white/10 rounded-lg p-4 border border-[#17E6B3]/20">
                  <h4 className="font-semibold mb-2 text-[#17E6B3]">Nossos Valores:</h4>
                  <ul className="space-y-1 text-sm text-[#B8C4E3]">
                    <li>• Ler a Bíblia</li>
                    <li>• Orar</li>
                    <li>• Jejuar</li>
                    <li>• Amar</li>
                    <li>• Servir</li>
                    <li>• Edificar</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ministérios */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nossos Ministérios
            </h2>
            <p className="text-[#B8C4E3] text-lg max-w-2xl mx-auto">
              Descubra como você pode servir e crescer espiritualmente em nossos diversos ministérios.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ministerios.map((ministerio, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl p-6 text-center hover:shadow-2xl hover:shadow-[#17E6B3]/20 transition-all duration-300"
              >
                <div className="text-4xl mb-4">{ministerio.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {ministerio.title}
                </h3>
                <p className="text-[#B8C4E3]">{ministerio.description}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/ministerios" className="border-2 border-[#17E6B3] text-[#17E6B3] hover:bg-[#17E6B3] hover:text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center">
              Ver Todos os Ministérios
            </Link>
          </div>
        </div>
      </section>

      {/* Eventos/Agenda */}
      <section className="section-padding bg-[#030B2E]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nossa Programação
            </h2>
            <p className="text-[#B8C4E3] text-lg max-w-2xl mx-auto">
              Participe dos nossos cultos e atividades semanais. Toda a família é bem-vinda!
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {eventos.map((evento, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm border border-[#17E6B3]/20 rounded-xl p-6"
              >
                <div className="flex items-center mb-4">
                  <Calendar className="text-[#17E6B3] mr-3" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {evento.titulo}
                    </h3>
                    <p className="text-[#17E6B3] font-medium">
                      {evento.data} às {evento.horario}
                    </p>
                  </div>
                </div>
                <p className="text-[#B8C4E3]">{evento.descricao}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/agenda" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center shadow-lg shadow-[#17E6B3]/30">
              Ver Agenda Completa
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-gradient-to-br from-[#030B2E] to-[#17E6B3]/20 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Venha Fazer Parte da Nossa Família
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-[#B8C4E3]">
              Você é muito bem-vindo! Junte-se a nós em nossa jornada de fé, 
              crescimento e serviço ao Reino de Deus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato" className="bg-[#17E6B3] hover:bg-[#17E6B3]/90 text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center">
                <MapPin className="mr-2" size={20} />
                Como Chegar
              </Link>
              <a href="tel:61 99818-7484" className="border-2 border-white text-white hover:bg-white hover:text-[#030B2E] font-semibold py-3 px-8 rounded-xl transition-all inline-flex items-center justify-center">
                <Phone className="mr-2" size={20} />
                (61) 99818-7484
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modal de vídeo */}
      <VideoModal 
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.id}
        title={selectedVideo?.titulo}
      />
    </div>
  );
};

export default Home;